import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

import {
  getStockSources,
  patchItem,
  type CommentView,
  type InspectionDetail,
  type ItemPatch,
  type ItemView,
  type PhotoView,
  type ReturnUnit,
  type SampleTransferView,
  type Verdict,
} from "@/api/okk_receiving";
import { DateInput } from "@/components/DateInput";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { fmtDate, fmtDateTime, ruNum } from "@/lib/format";
import {
  authorName,
  isNotRequiredOnOkk,
  parseDecimal,
  RETURN_UNIT_TITLE,
  TARGET_UNASSIGNED,
  todayIso,
  verdictInfo,
  verdictsFor,
  VERDICTS_NEED_NOTE,
} from "@/lib/okk-receiving";
import { apiErrorText } from "@/lib/scan-ui/screen";
import { cn } from "@/lib/utils";
import { CommentForm, CommentList } from "@/pages/okk-receiving/OkkComments";
import { OkkPhotoBlock } from "@/pages/okk-receiving/OkkPhotos";
import { SampleSourceField, transferUnit } from "@/pages/okk-receiving/OkkSamples";

/** Черновик вердикта: то, что человек выбрал/набрал, но ещё не сохранил. */
interface VerdictDraft {
  verdict: Verdict | null;
  note: string;
  testUntil: string;
  returnQty: string;
  returnUnit: ReturnUnit | null;
}

function draftOf(item: ItemView): VerdictDraft {
  return {
    verdict: item.verdict,
    note: item.verdict_note ?? "",
    testUntil: item.test_until ?? "",
    returnQty: item.return_qty === null ? "" : String(item.return_qty).replace(".", ","),
    returnUnit: item.return_unit,
  };
}

function sameDraft(a: VerdictDraft, b: VerdictDraft): boolean {
  return (
    a.verdict === b.verdict &&
    a.note.trim() === b.note.trim() &&
    (a.verdict !== "testing" || a.testUntil === b.testUntil) &&
    (a.verdict !== "return" || (a.returnQty === b.returnQty && a.returnUnit === b.returnUnit))
  );
}

function samplesText(n: number): string {
  return n ? String(n).replace(".", ",") : "0";
}

/** Набранное в поле образцов не равно сохранённому (мусор — тоже «не сохранено»). */
function qtyUnsaved(text: string, saved: number): boolean {
  const parsed = parseDecimal(text);
  const value = parsed === null ? 0 : parsed.value;
  return Number.isNaN(value) || value !== saved;
}

type QtyField = "samples_archive_qty" | "samples_test_qty";

/** Что у позиции не сохранено: страница гасит по этому «Завершить» и «Оформить». */
export interface ItemUnsaved {
  /** Вердикт и его поля: набраны, но не сохранены или сохранение в пути. */
  verdict: boolean;
  /** Образцы: набраны, но не отправлены, или запрос ещё идёт. */
  samples: boolean;
}

/** Вердикты без обязательных полей сохраняются сразу по нажатию. */
function savesAtOnce(v: Verdict): boolean {
  return v === "in_work" || v === "not_required";
}

/** Почему черновик нельзя сохранить — тем же языком, что и сервер. Пусто — можно. */
function draftProblem(d: VerdictDraft, item: ItemView, testDateErr: string): string {
  if (d.verdict === null) return "";
  if (VERDICTS_NEED_NOTE.has(d.verdict) && !d.note.trim()) {
    return "Нужен комментарий: что не так и что делать";
  }
  if (d.verdict === "testing") {
    if (testDateErr) return testDateErr;
    if (!d.testUntil) return "Укажите, до какого числа идёт тест";
    const changed = item.verdict !== "testing" || item.test_until !== d.testUntil;
    if (changed && d.testUntil < todayIso()) return "Срок теста уже прошёл — сегодня или позже";
  }
  if (d.verdict === "return") {
    const q = parseDecimal(d.returnQty);
    if (!q || Number.isNaN(q.value) || q.value <= 0) return "Количество возврата — число больше нуля";
    if (!d.returnUnit) return "Выберите: штуки или места";
  }
  return "";
}

function patchOf(d: VerdictDraft): ItemPatch {
  const body: ItemPatch = { verdict: d.verdict, verdict_note: d.note.trim() || null };
  if (d.verdict === "testing") body.test_until = d.testUntil;
  if (d.verdict === "return") {
    body.return_qty = parseDecimal(d.returnQty)?.text ?? null;
    body.return_unit = d.returnUnit;
  }
  return body;
}

/**
 * Позиция проверки: что пришло (снимок партии), вердикт крупными кнопками,
 * поля вердикта, образцы, фото и комментарии к позиции.
 *
 * Сохранение — по месту (PATCH). Вердикт без полей («в работу», «не требует
 * проверки») уходит сразу; остальные — кнопкой «Сохранить», пока не заполнено
 * обязательное, она погашена с причиной. Отказ сервера — текстом под формой,
 * ввод при этом НЕ сбрасывается.
 */
export function OkkItemCard({
  inspectionId,
  item,
  transfer,
  done,
  photos,
  comments,
  onSaved,
  onChanged,
  onOpenPhoto,
  onUnsaved,
}: {
  inspectionId: string;
  item: ItemView;
  /** Документ 1С, которым оформлены образцы «в архив» позиции (если оформлены). */
  transfer: SampleTransferView | null;
  /** Проверка завершена — правки позиций и фото заблокированы. */
  done: boolean;
  photos: PhotoView[];
  comments: CommentView[];
  /** Сервер вернул карточку целиком после PATCH. */
  onSaved: (detail: InspectionDetail) => void;
  /** Перечитать карточку (фото/комментарии). */
  onChanged: () => void;
  onOpenPhoto: (index: number) => void;
  /** Состояние «есть несохранённое» (`null` — всё сохранено); на размонтировании — `null`. */
  onUnsaved: (itemId: string, state: ItemUnsaved | null) => void;
}): React.ReactElement {
  const removed = item.removed_from_delivery;
  const locked = done || removed;
  const unassigned = item.target === TARGET_UNASSIGNED;

  const [draft, setDraft] = useState<VerdictDraft>(() => draftOf(item));
  const [testDateErr, setTestDateErr] = useState("");
  const [verdictErr, setVerdictErr] = useState("");
  const [archive, setArchive] = useState(() => samplesText(item.samples_archive_qty));
  const [test, setTest] = useState(() => samplesText(item.samples_test_qty));
  const [archiveErr, setArchiveErr] = useState("");
  const [testErr, setTestErr] = useState("");
  const [sourceErr, setSourceErr] = useState("");
  // Остатки из 1С нужны, когда образцы «в архив» есть или их начали вводить
  // (тогда же видна единица). Не на каждую позицию при открытии карточки —
  // это поход в 1С на позицию.
  const [archiveTouched, setArchiveTouched] = useState(false);
  const transferred = item.samples_transferred;
  const sourcesQ = useQuery({
    queryKey: ["okk-stock-sources", inspectionId, item.id],
    queryFn: () => getStockSources(inspectionId, item.id),
    enabled: !done && !removed && !transferred && (item.samples_archive_qty > 0 || archiveTouched),
    retry: false,
    refetchOnWindowFocus: false,
  });
  // Единица образцов «в архив» — БАЗОВАЯ единица номенклатуры: из остатков 1С
  // или из строки документа. Пока не знаем — без единицы, «шт» позиции не
  // подставляем (у позиции может быть упаковка).
  const archiveUnit = transferred
    ? transferUnit(transfer, item.id)
    : (sourcesQ.data?.unit_name ?? null);

  // Карточку перечитали (сохранили соседнюю позицию, фото, образцы): то, что
  // человек не трогал, обновляем с сервера, а набранное — не затираем.
  const prev = useRef(item);
  useEffect(() => {
    const was = prev.current;
    prev.current = item;
    if (was === item) return;
    setDraft((d) => (sameDraft(d, draftOf(was)) ? draftOf(item) : d));
    setArchive((t) => (t === samplesText(was.samples_archive_qty) ? samplesText(item.samples_archive_qty) : t));
    setTest((t) => (t === samplesText(was.samples_test_qty) ? samplesText(item.samples_test_qty) : t));
  }, [item]);

  // Мутации — с обработчиками в ОПЦИЯХ хука, не в `mutate(…, { onError })`:
  // колбэк, переданный в `mutate`, TanStack v5 теряет, если до ответа вызван
  // следующий `mutate` — и отказ сервера пропадал молча.
  //
  // Две мутации вердикта/образцов, а не одна: ответ на сохранение образцов не
  // должен затирать вердикт, который человек выбрал, пока образцы летели.
  const save = useMutation({
    mutationFn: ({ body }: { draft: VerdictDraft; body: ItemPatch }) =>
      patchItem(inspectionId, item.id, body),
    onMutate: () => setVerdictErr(""),
    onSuccess: (detail, { draft: sent }) => {
      const fresh = detail.items.find((i) => i.id === item.id);
      // Набранное ПОСЛЕ отправки (комментарий, пока летел «В работу») не трогаем.
      if (fresh) setDraft((d) => (sameDraft(d, sent) ? draftOf(fresh) : d));
      onSaved(detail);
    },
    onError: (e) => setVerdictErr(apiErrorText(e)),
  });

  // Поле → что сейчас отправляется. Ref — для повтора в том же такте (Enter и
  // сразу blur), state — чтобы погасить поле и показать «не сохранено».
  const sendingRef = useRef<Partial<Record<QtyField, string>>>({});
  const [sending, setSending] = useState<Partial<Record<QtyField, string>>>({});
  const markSending = (field: QtyField, text: string | null) => {
    if (text === null) delete sendingRef.current[field];
    else sendingRef.current[field] = text;
    setSending({ ...sendingRef.current });
  };
  const qtyErrSetter = (field: QtyField) =>
    field === "samples_archive_qty" ? setArchiveErr : setTestErr;
  const saveQty = useMutation({
    mutationFn: ({ body }: { field: QtyField; body: ItemPatch }) =>
      patchItem(inspectionId, item.id, body),
    onSuccess: (detail) => onSaved(detail),
    onError: (e, { field }) => qtyErrSetter(field)(apiErrorText(e)),
    onSettled: (_d, _e, { field }) => markSending(field, null),
  });

  const saveSource = useMutation({
    mutationFn: (ref: string) =>
      patchItem(inspectionId, item.id, { samples_source_warehouse_ref: ref }),
    onMutate: () => setSourceErr(""),
    onSuccess: (detail) => onSaved(detail),
    onError: (e) => setSourceErr(apiErrorText(e)),
  });

  const saveVerdict = (d: VerdictDraft) => save.mutate({ draft: d, body: patchOf(d) });

  const saveSamples = (field: QtyField, raw: string) => {
    const setErr = qtyErrSetter(field);
    const current = field === "samples_archive_qty" ? item.samples_archive_qty : item.samples_test_qty;
    const parsed = parseDecimal(raw);
    const value = parsed === null ? 0 : parsed.value;
    if (Number.isNaN(value)) {
      setErr("Число, например 3 или 1,5 (пусто — 0)");
      return;
    }
    setErr("");
    if (value === current) return;
    const text = parsed === null ? "0" : parsed.text;
    // Enter и следом blur — одно значение: второй PATCH не шлём.
    if (sendingRef.current[field] === text) return;
    markSending(field, text);
    saveQty.mutate({ field, body: { [field]: text } });
  };

  const pick = (v: Verdict) => {
    const next = { ...draft, verdict: v };
    setDraft(next);
    setVerdictErr("");
    if (savesAtOnce(v) && v !== item.verdict) saveVerdict(next);
  };

  const dirty = !sameDraft(draft, draftOf(item));
  const verdictUnsaved = !locked && (dirty || save.isPending);
  const samplesUnsaved =
    !locked &&
    (qtyUnsaved(archive, item.samples_archive_qty) ||
      qtyUnsaved(test, item.samples_test_qty) ||
      Object.keys(sending).length > 0 ||
      saveSource.isPending);
  useEffect(() => {
    onUnsaved(
      item.id,
      verdictUnsaved || samplesUnsaved ? { verdict: verdictUnsaved, samples: samplesUnsaved } : null,
    );
  }, [item.id, verdictUnsaved, samplesUnsaved, onUnsaved]);
  useEffect(() => () => onUnsaved(item.id, null), [item.id, onUnsaved]);
  const shownVerdict = locked ? item.verdict : draft.verdict;
  const misplaced = !locked && isNotRequiredOnOkk(item);
  const problem = draftProblem(draft, item, testDateErr);
  const needNote = draft.verdict !== null && VERDICTS_NEED_NOTE.has(draft.verdict);

  return (
    <Card
      data-testid="okk-item"
      className={cn("space-y-3 p-3", removed && "bg-muted/50 opacity-70")}
    >
      <div className="space-y-1">
        <div className="flex flex-wrap items-start gap-2">
          <div className="min-w-0 flex-1 font-medium break-words">{item.nomenclature_name}</div>
          <div className="shrink-0 text-sm tabular-nums">
            {ruNum.format(item.qty)} {item.unit_name}
          </div>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          {item.article && <span>арт. {item.article}</span>}
          {item.characteristic_name && <span>{item.characteristic_name}</span>}
          {item.supplier_batch && <span>партия поставщика: {item.supplier_batch}</span>}
          {item.group_path && <span>группа: {item.group_path}</span>}
        </div>
        {removed && (
          <div className="text-xs font-medium text-muted-foreground">
            {item.removed_reason === "gone"
              ? "Больше нет в приходе"
              : item.removed_reason === "rerouted"
                ? "Позиция больше не на ОКК (изменилось правило маршрута)"
                : "Убрана из прихода"}{" "}
            — решение сохранено, правка недоступна
          </div>
        )}
        {misplaced && (
          <div
            role="alert"
            className="flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-900"
          >
            <AlertTriangle className="size-3.5 shrink-0" />
            Выберите вердикт: позиция теперь на ОКК, прежнее «не требует проверки» неуместно
          </div>
        )}
        {unassigned && !removed && (
          <div className="flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-900">
            <AlertTriangle className="size-3.5 shrink-0" />
            Группа номенклатуры не настроена — позиция пришла в ОКК без правила
          </div>
        )}
      </div>

      <div role="radiogroup" aria-label="Вердикт" className="grid grid-cols-2 gap-1.5 sm:flex sm:flex-wrap">
        {verdictsFor(item).map((v) => {
          const info = verdictInfo(v);
          const active = shownVerdict === v;
          return (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={locked || save.isPending}
              onClick={() => pick(v)}
              className={cn(
                "min-h-11 rounded-md border px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed",
                v === "not_required" && "col-span-2",
                active
                  ? info.tone === "bad"
                    ? "border-rose-600 bg-rose-600 text-white"
                    : info.tone === "warn"
                      ? "border-amber-500 bg-amber-500 text-white"
                      : info.tone === "ok"
                        ? "border-emerald-600 bg-emerald-600 text-white"
                        : "border-slate-600 bg-slate-600 text-white"
                  : "bg-background hover:bg-accent disabled:opacity-60",
              )}
            >
              {info.button}
            </button>
          );
        })}
      </div>

      {draft.verdict !== null && !locked && (
        <div className="space-y-2">
          {draft.verdict === "testing" && (
            <div>
              <span className="mb-1 block text-xs text-muted-foreground">На тесте до</span>
              <DateInput
                value={draft.testUntil}
                ariaLabel="На тесте до"
                className="w-44"
                onChange={(iso, err) => {
                  setTestDateErr(err);
                  setDraft((d) => ({ ...d, testUntil: iso }));
                }}
              />
            </div>
          )}
          {draft.verdict === "return" && (
            <div className="flex flex-wrap items-end gap-2">
              <label className="block">
                <span className="mb-1 block text-xs text-muted-foreground">Вернуть</span>
                <input
                  inputMode="decimal"
                  value={draft.returnQty}
                  onChange={(e) => setDraft((d) => ({ ...d, returnQty: e.target.value }))}
                  aria-label="Количество возврата"
                  className="h-10 w-28 rounded-md border bg-background px-2 text-sm"
                />
              </label>
              <div role="radiogroup" aria-label="Единица возврата" className="flex gap-1">
                {(["pcs", "places"] as ReturnUnit[]).map((u) => (
                  <button
                    key={u}
                    type="button"
                    role="radio"
                    aria-checked={draft.returnUnit === u}
                    onClick={() => setDraft((d) => ({ ...d, returnUnit: u }))}
                    className={cn(
                      "h-10 rounded-md border px-3 text-sm",
                      draft.returnUnit === u ? "border-primary bg-primary text-primary-foreground" : "bg-background",
                    )}
                  >
                    {u === "pcs" ? "шт" : "места"}
                  </button>
                ))}
              </div>
            </div>
          )}
          <label className="block">
            <span className="mb-1 block text-xs text-muted-foreground">
              Комментарий к вердикту{needNote ? " (обязательно)" : ""}
            </span>
            <textarea
              value={draft.note}
              onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))}
              aria-label="Комментарий к вердикту"
              rows={2}
              className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
              placeholder={needNote ? "Что не так и что делать" : "Необязательно"}
            />
          </label>
          {dirty && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                disabled={Boolean(problem) || save.isPending}
                onClick={() => saveVerdict(draft)}
              >
                {save.isPending && <Loader2 className="animate-spin" />}
                Сохранить вердикт
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={save.isPending}
                onClick={() => {
                  setDraft(draftOf(item));
                  setVerdictErr("");
                  setTestDateErr("");
                }}
              >
                Отменить
              </Button>
              {problem && <span className="text-xs text-amber-800">{problem}</span>}
            </div>
          )}
        </div>
      )}
      {locked && item.verdict !== null && <VerdictSummary item={item} />}
      {verdictErr && (
        <div role="alert" className="text-sm text-rose-700">
          {verdictErr}
        </div>
      )}

      <div className="grid gap-2 sm:grid-cols-2">
        <SamplesField
          label="Образцы в архив"
          unit={archiveUnit}
          value={archive}
          error={archiveErr}
          disabled={locked || transferred || sending.samples_archive_qty !== undefined}
          onChange={setArchive}
          onFocus={() => setArchiveTouched(true)}
          onCommit={() => saveSamples("samples_archive_qty", archive)}
        />
        <SamplesField
          label="Образцы на тест"
          unit="шт"
          value={test}
          error={testErr}
          disabled={locked || sending.samples_test_qty !== undefined}
          onChange={setTest}
          onCommit={() => saveSamples("samples_test_qty", test)}
        />
      </div>
      {!removed && (item.samples_archive_qty > 0 || transferred) && (
        <SampleSourceField
          item={item}
          transfer={transfer}
          sources={sourcesQ.data}
          sourcesLoading={sourcesQ.isFetching}
          sourcesError={sourcesQ.error}
          onRetry={() => void sourcesQ.refetch()}
          disabled={locked}
          saving={saveSource.isPending}
          error={sourceErr}
          onPick={(ref) => saveSource.mutate(ref)}
        />
      )}

      {item.updated_by && item.updated_at && (
        <div className="text-[11px] text-muted-foreground">
          изменил {authorName(item.updated_by)} · {fmtDateTime(item.updated_at)}
        </div>
      )}

      <div className="space-y-1">
        <div className="text-xs font-medium text-muted-foreground">Фото позиции</div>
        <OkkPhotoBlock
          inspectionId={inspectionId}
          itemId={item.id}
          title={`Фото: ${item.nomenclature_name}`}
          photos={photos}
          locked={locked}
          onOpen={onOpenPhoto}
          onChanged={onChanged}
        />
      </div>

      <details className="text-sm" open={comments.length > 0}>
        <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
          Комментарии к позиции ({comments.length})
        </summary>
        <div className="mt-2 space-y-2">
          <CommentList inspectionId={inspectionId} comments={comments} onChanged={onChanged} />
          {!removed && (
            <CommentForm
              inspectionId={inspectionId}
              itemId={item.id}
              onAdded={onChanged}
              placeholder="Комментарий к позиции"
            />
          )}
        </div>
      </details>
    </Card>
  );
}

function VerdictSummary({ item }: { item: ItemView }): React.ReactElement {
  return (
    <div className="space-y-0.5 text-sm">
      {item.verdict === "testing" && item.test_until && (
        <div>на тесте до {fmtDate(item.test_until)}</div>
      )}
      {item.verdict === "return" && item.return_qty !== null && item.return_unit && (
        <div>
          вернуть {ruNum.format(item.return_qty)} {RETURN_UNIT_TITLE[item.return_unit]}
        </div>
      )}
      {item.verdict_note && <div className="whitespace-pre-wrap">{item.verdict_note}</div>}
    </div>
  );
}

function SamplesField({
  label,
  unit,
  value,
  error,
  disabled,
  onChange,
  onFocus,
  onCommit,
}: {
  label: string;
  /** `null` — единица ещё не известна: показываем без неё, не угадываем. */
  unit: string | null;
  value: string;
  error: string;
  disabled: boolean;
  onChange: (v: string) => void;
  onFocus?: () => void;
  onCommit: () => void;
}): React.ReactElement {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-muted-foreground">
        {label}
        {unit ? `, ${unit}` : ""}
      </span>
      <input
        inputMode="decimal"
        value={value}
        disabled={disabled}
        aria-label={label}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onCommit}
        onKeyDown={(e) => {
          if (e.key === "Enter") onCommit();
        }}
        className="h-10 w-full rounded-md border bg-background px-2 text-sm disabled:opacity-60"
      />
      {error && <span className="mt-1 block text-xs text-rose-700">{error}</span>}
    </label>
  );
}
